import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import axiosInstance from "../../../../../api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ApexCharts from "apexcharts";

const ResourceUtilizationChart = () => {
  const [chartData, setChartData] = useState({
    categories: [],
    ambulance: [],
    paramedics: [],
  });
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [chartInstance, setChartInstance] = useState(null);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const response = await axiosInstance.get("/ems/get-resource-by-type");
        const data = response.data;
        const newChartData = {
          categories: data.week_labels || [],
          ambulance: data.ambulance || [],
          paramedics: data.paramedics || [],
        };
        setChartData(newChartData);

        // Check for no-data condition (empty arrays or all zeros)
        const isEmpty = newChartData.categories.length === 0;
        const allZero = newChartData.ambulance.every(v => v === 0) && newChartData.paramedics.every(v => v === 0);
        setNoData(isEmpty || allZero);
      } catch (error) {
        console.error("Error fetching chart data:", error);
        setChartData({ categories: [], ambulance: [], paramedics: [] });
        setNoData(true);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, []);

  const exportPDF = async () => {
    try {
      const dataURI = await ApexCharts.exec("resource-utilization", "dataURI", {
        scale: 2,
        width: 800,
      });

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 10;
      const currentYear = new Date().getFullYear();

      // Add chart image
      const chartHeight = 100;
      const chartWidth = pageWidth - 2 * margin;
      const chartTopMargin = 40;
      doc.addImage(dataURI.imgURI, "PNG", margin, chartTopMargin, chartWidth, chartHeight);

      // Define table options
      const options = {
        startY: chartTopMargin + chartHeight + 10,
        head: [["Week", "Ambulance", "Paramedics"]],
        body: chartData.categories.map((label, index) => [
          label,
          chartData.ambulance[index]?.toFixed(0) || "0",
          chartData.paramedics[index]?.toFixed(0) || "0",
        ]),
        theme: "grid",
        headStyles: {
          fillColor: [0, 123, 255], // Blue header for consistency
          textColor: 255,
          fontStyle: "bold",
          fontSize: 10,
        },
        styles: {
          fontSize: 9,
          cellPadding: 2,
          overflow: "linebreak",
          minCellWidth: 20,
        },
        columnStyles: {
          0: { cellWidth: (pageWidth - 2 * margin) * 0.4 }, // Week
          1: { cellWidth: (pageWidth - 2 * margin) * 0.3 }, // Ambulance
          2: { cellWidth: (pageWidth - 2 * margin) * 0.3 }, // Paramedics
        },
        margin: { left: margin, right: margin },
        didDrawPage: (data) => {
          // Header - Main title
          doc.setFontSize(16);
          doc.setFont("helvetica", "bold");
          doc.text("Resource Utilization Report", pageWidth / 2, 15, {
            align: "center",
          });

          // Header - Period information
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.text(`Year: ${currentYear}`, pageWidth / 2, 25, {
            align: "center",
          });

          // Header - Generated timestamp
          doc.setFontSize(8);
          doc.text(
            "Generated on: October 10, 2025, 11:17 AM IST",
            pageWidth / 2,
            33,
            { align: "center" }
          );

          // Footer - Page number
          doc.setFontSize(8);
          doc.text(
            `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: "center" }
          );
        },
      };

      // Add table only if data exists
      if (chartData.categories.length > 0 && !noData) {
        autoTable(doc, options);
      } else {
        // Add message if no data
        doc.setFontSize(10);
        doc.text(
          "No data available for the selected period.",
          pageWidth / 2,
          chartTopMargin + chartHeight + 20,
          { align: "center" }
        );
      }

      doc.save(`resource_utilization_${currentYear}.pdf`);
    } catch (error) {
      console.error("Error exporting PDF:", error);
    }
  };

  const exportXML = () => {
    const currentYear = new Date().getFullYear();
    let xmlData = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xmlData += `<ResourceUtilization year="${currentYear}">\n`;

    if (chartData.categories.length > 0 && !noData) {
      chartData.categories.forEach((label, index) => {
        xmlData += `  <Week>\n`;
        xmlData += `    <Label>${label}</Label>\n`;
        xmlData += `    <Ambulance>${chartData.ambulance[index]?.toFixed(0) || '0'}</Ambulance>\n`;
        xmlData += `    <Paramedics>${chartData.paramedics[index]?.toFixed(0) || '0'}</Paramedics>\n`;
        xmlData += `  </Week>\n`;
      });
    } else {
      xmlData += `  <Message>No data available for the selected period.</Message>\n`;
    }
    xmlData += `</ResourceUtilization>`;

    const blob = new Blob([xmlData], { type: "application/xml" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `resource_utilization_${currentYear}.xml`;
    link.click();
  };

  const downloadGraph = async () => {
    try {
      const dataURI = await ApexCharts.exec("resource-utilization", "dataURI", {
        scale: 2,
        width: 1200,
      });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height + 70;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.fillStyle = '#000000';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Resource Utilization', canvas.width / 2, 30);

        ctx.font = '16px Arial';
        ctx.fillText(`Year: ${new Date().getFullYear()}`, canvas.width / 2, 50);

        ctx.font = '12px Arial';
        ctx.fillText('Generated on: October 10, 2025, 11:17 AM IST', canvas.width / 2, 65);

        ctx.drawImage(img, 0, 75, img.width, img.height);

        const link = document.createElement('a');
        link.download = `resource_utilization_${new Date().getFullYear()}.png`;
        link.href = canvas.toDataURL('image/png');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      img.src = dataURI.imgURI;
    } catch (error) {
      console.error("Error downloading graph:", error);
    }
  };

  const chartOptions = {
    chart: {
      id: "resource-utilization",
      type: "bar",
      height: 250,
      fontFamily: "Plus Jakarta Sans, sans-serif",
      foreColor: "#adb0bb",
      toolbar: {
        show: false,
        export: {
          png: {
            filename: `resource_utilization_${new Date().getFullYear()}`,
          },
        },
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
      events: {
        mounted: function(chartContext, config) {
          setChartInstance(chartContext);
        },
        updated: function(chartContext, config) {
          setChartInstance(chartContext);
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "50%",
        borderRadius: 6,
        borderRadiusApplication: "end",
        dataLabels: {
          position: 'top',
        },
      },
    },
    colors: ["#11DEB9", "#FFB53B"],
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    dataLabels: {
      enabled: true,
      formatter: function(val) {
        return val ? val.toFixed(0) : '0';
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#304758"],
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      fontSize: '12px',
    },
    grid: {
      show: false,
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
    },
    yaxis: {
      min: 0,
      max: Math.ceil(Math.max(...chartData.ambulance, ...chartData.paramedics, 10) / 5) * 5,
      tickAmount: 4,
      labels: {
        formatter: (val) => val.toFixed(0),
        style: {
          colors: '#6b7280',
          fontSize: '12px',
        },
      },
      title: {
        text: 'Resources Utilized',
        style: {
          color: '#6b7280',
          fontSize: '12px',
        },
      },
    },
    xaxis: {
      categories: chartData.categories,
      axisTicks: { show: false },
      labels: {
        style: {
          colors: '#6b7280',
          fontSize: '12px',
        },
      },
    },
    tooltip: {
      theme: "dark",
      x: { show: true },
      y: {
        formatter: function(val) {
          return val ? `${val.toFixed(0)}` : '0';
        },
      },
      style: {
        fontSize: '12px',
      },
    },
    states: {
      hover: {
        filter: {
          type: 'darken',
          value: 0.8,
        },
      },
    },
  };

  const series = [
    { name: "Ambulance", data: chartData.ambulance },
    { name: "Paramedics", data: chartData.paramedics },
  ];

  if (loading) return <p>Loading chart...</p>;

  return (
    <div style={{ height: "350px" }}>
      <div className="mb-3 widget-top-panel">
        <div>
          <h5 className="mb-0 w-100">Resources by Type</h5>
        </div>
        <div className="dropdown">
          <button
            className="btn btn-outline-primary dropdown-toggle"
            type="button"
            id="exportDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            Export Options
          </button>
          <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="exportDropdown">
            <li>
              <button className="dropdown-item" onClick={exportPDF}>
                Export PDF
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={exportXML}>
                Export XML
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={downloadGraph}>
                Download Graph
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div style={{ height: "250px" }}>
        <Chart
          options={chartOptions}
          series={series}
          type="bar"
          height="100%"
        />
      </div>
      {noData && (
        <div className="text-center mt-3 fw-semibold text-muted">
          Data is Not Available
        </div>
      )}
    </div>
  );
};

export default ResourceUtilizationChart;